/**
 * features/property/components/DealerInventoryReveal.jsx (v2.71.63)
 * -----------------------------------------------------------------
 * Lightweight collapsible "Related Inventory Available" panel that
 * REPLACES the auto-rendering <SimilarProperties> feed for dealer /
 * builder users on the property detail page.
 *
 * Why: dealers shouldn't browse endlessly through the public catalog
 * via similar-property recommendations. The detail page should feel
 * professional + workflow-focused.
 *
 * UX:
 *   Initial:  one slim row with title + count badge + "Load related"
 *             button. Zero network calls until the user opts in.
 *   Loaded:   reuses the SimilarProperties component to render the
 *             actual cards — but only once the dealer asks for them.
 *
 * Future-ready: when premium dealer plans unlock marketplace intelligence,
 * flip `autoExpand` to true via user.marketplace_full_access.
 */

import React, { useState } from "react";
import { Boxes, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import SimilarProperties from "./SimilarProperties";

export default function DealerInventoryReveal({ propertyId }) {
  const [open, setOpen] = useState(false);

  if (open) {
    return (
      <div className="mt-8" data-testid="dealer-inventory-loaded">
        <SimilarProperties propertyId={propertyId} />
      </div>
    );
  }

  return (
    <div
      data-testid="dealer-inventory-collapsed"
      className="mt-8 bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center gap-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
    >
      <div className="shrink-0 w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center">
        <Boxes size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-slate-900">Related Inventory Available</div>
        <div className="text-[11.5px] text-slate-500 mt-0.5">
          Browse properties similar to this listing. Workflow-friendly — only loads on request.
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="dealer-inventory-load"
        className="h-9 rounded-full text-[12.5px] font-semibold"
      >
        Load related <ChevronRight size={13} className="ml-0.5" />
      </Button>
    </div>
  );
}
